import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { opcodeAssistantService } from 'shared/api';

interface AssistantSelectProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
}

interface AssistantOption {
    value: string;
    name: string;
}

function unwrapAssistants(res: any): AssistantOption[] {
    const raw = Array.isArray(res) ? res
        : Array.isArray(res?.data) ? res.data
        : Array.isArray(res?.data?.data) ? res.data.data
        : Array.isArray(res?.items) ? res.items
        : [];
    return raw
        .map((item: any) => ({
            value: item?._id ?? item?.assistant_id ?? item?.id ?? '',
            name: item?.title ?? item?.name ?? item?.assistant_id ?? item?._id ?? '',
        }))
        .filter((o: AssistantOption) => o.value);
}

const AssistantSelect: React.FC<AssistantSelectProps> = ({
    value,
    onChange,
    disabled,
    placeholder = 'Select Assistant...',
    className = '',
}) => {
    const { oragID, subOragID } = useParams<{ oragID: string; subOragID: string }>();
    const [assistants, setAssistants] = useState<AssistantOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError(false);
        opcodeAssistantService
            .listOpcodeAssistants(oragID || '', subOragID || '')
            .then((res: any) => {
                if (cancelled) return;
                setAssistants(unwrapAssistants(res));
            })
            .catch((err: unknown) => {
                console.error('listOpcodeAssistants failed:', err);
                if (!cancelled) setLoadError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [oragID, subOragID]);

    if (loading) {
        return (
            <div className={`h-9 flex items-center gap-2 px-3 rounded-md border border-input bg-background text-xs text-muted-foreground ${className}`}>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading assistants…
            </div>
        );
    }

    if (loadError) {
        return (
            <div className={`h-9 flex items-center px-3 rounded-md border border-destructive/40 bg-background text-xs text-destructive ${className}`}>
                Failed to load assistants — please refresh
            </div>
        );
    }

    if (assistants.length === 0) {
        return (
            <div className={`h-9 flex items-center px-3 rounded-md border border-input bg-background text-xs text-muted-foreground ${className}`}>
                No assistants found — create one in DE Studio first
            </div>
        );
    }

    return (
        <select
            className={`h-9 w-full rounded-md border border-input bg-background px-3 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 ${className}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
        >
            <option value="" disabled>{placeholder}</option>
            {assistants.map((a) => (
                <option key={a.value} value={a.value}>{a.name}</option>
            ))}
        </select>
    );
};

export default AssistantSelect;
